import {
    Box,
    ButtonBase,
    Chip,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListSubheader,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Add, ChevronRight, ExpandMore, Search } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ValueField } from "modules/record/record-producer/components/record-producer-fields/condition-field/ValueField";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { useChoiceOptions } from "modules/record/shared/hooks/useChoiceOptions";
import { useReferenceDisplayColumn, useReferenceOptions } from "modules/record/shared/hooks/useReferenceOptions";
import { getReferenceOptionLabel } from "modules/record/shared/components/ReferenceAutocompleteInput";
import { FlussValue, ScopeOption } from "./fluss-builder-types";
import { useDataCatalogContext } from "./data-picker/DataCatalogContext";
import useTableColumns, { loadTableColumns, useTableParents } from "./data-picker/useTableColumns";
import {
    buildPickerGroups,
    CURRENT_DATE_UNITS,
    CurrentDateUnit,
    DataPickerMeta,
    ExtraGroup,
    isDataReference,
    MAX_PATH_DEPTH,
    outputExpandable,
    outputSelectable,
    PickerGroup,
    PickerOutput,
    pickerOutputLabel,
    parseCurrentDateToken,
    scopeOptionToOutput,
    useDataPathInfo,
} from "./data-picker/dataPicker";
import { FlussDataCatalog } from "modules/fluss/types/data-catalog";
import { listFlussMetadata } from "./node-properties/useFlussNodePanelMetadata";
import { pickerTypeLabel } from "./data-picker/typeLabel";
import TemplateTextField from "./TemplateTextField";

// Column types delegated to the platform's own ValueField/FIELD_COMPONENT_MAP for the fixed value:
// they need the real sys_column (reference_filter, CHOICE options, currency) to be usable.
const VALUE_FIELD_TYPES = ["REFERENCE", "CHOICE", "CURRENCY"];
const normalizeSearch = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
const asValueFieldColumn = (column: NonNullable<ScopeOption["column"]>) => ({ ...column, name: "value" });
const normalizeLiteralValue = (raw: unknown): FlussValue => {
    if (raw === undefined || raw === null) return null;
    if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") return raw;
    return null;
};

const defaultLiteral = (type?: string): FlussValue => {
    if (type === "BOOLEAN") return false;
    if (type === "CURRENCY") return 0;
    if (["INTEGER", "DECIMAL", "FLOAT"].includes(type || "")) return 0;
    if (["REFERENCE", "CHOICE"].includes(type || "")) return null;
    return "";
};

export interface DataPickerProps {
    value: FlussValue | undefined;
    // `undefined` clears the value back to "not mapped" (it is how a mapping row is unset).
    onChange: (value: FlussValue | undefined, meta?: DataPickerMeta) => void;
    // C9 type filter: TEXT accepts scalar values; references are navigable but not selectable.
    expectedType?: string;
    allowedTypes?: string[];
    requireRecordTable?: boolean;
    // C9 table filter: only records of this table (navigation through other records stays open).
    referencedTable?: string;
    // The destination column, when there is a real one: unlocks the typed "Valor fixo" widgets.
    destinationField?: ScopeOption;
    allowFixed?: boolean;
    // Keep unrestricted source selection while using a composed-text editor for a fixed value.
    literalTemplate?: boolean;
    includeRecord?: boolean;
    // Only `record.<column>` of `recordTable` (observed-table conditions, no process data).
    recordOnly?: boolean;
    // `record.` of this node is a table other than the process anchor (work record, signal table).
    recordTable?: string;
    recordSourceLabel?: string;
    extraGroups?: ExtraGroup[];
    catalog?: FlussDataCatalog;
    error?: boolean;
    helperText?: string;
    ariaLabel?: string;
    disabled?: boolean;
    fieldPath?: string;
    // Reports what the current reference resolves to (labels/type) once known.
    onResolved?: (info: DataPickerMeta) => void;
}

interface RowsProps {
    outputs: PickerOutput[];
    expectedType?: string;
    allowedTypes?: string[];
    requireRecordTable?: boolean;
    referencedTable?: string;
    tableParents: Record<string, string | undefined>;
    selectedPath: string;
    search: string;
    onPick: (output: PickerOutput) => void;
    onRowsChanged?: () => void;
}

const ChildRows = ({ output, onRowsChanged, ...rest }: { output: PickerOutput } & Omit<RowsProps, "outputs" | "search">) => {
    const { t } = useTranslation();
    const { columns, loading } = useTableColumns(output.referencedTable);
    const children = useMemo(() => columns.map((column): PickerOutput => ({
        ...scopeOptionToOutput(column),
        path: `${output.path}.${column.name}`,
        depth: output.depth + 1,
    })), [columns, output.depth, output.path]);
    useEffect(() => { if (!loading) onRowsChanged?.(); }, [loading, columns, onRowsChanged]);
    if (loading) {
        return <Typography variant="caption" color="text.secondary" sx={{ pl: 2 * (output.depth + 1) + 2 }}>
            {t("fluss.data.picker.loading", { defaultValue: "Carregando campos…" })}
        </Typography>;
    }
    return <OutputRows outputs={children} search="" onRowsChanged={onRowsChanged} {...rest} />;
};

const OutputRows = ({ outputs, expectedType, allowedTypes, requireRecordTable, referencedTable, tableParents, selectedPath, search, onPick, onRowsChanged }: RowsProps) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    useEffect(() => { onRowsChanged?.(); }, [expanded, onRowsChanged]);
    const needle = normalizeSearch(search.trim());
    return (
        <>
            {outputs.map((output) => {
                const label = pickerOutputLabel(t, output);
                if (needle && !normalizeSearch(`${label} ${output.key}`).includes(needle)) return null;
                const selectable = (!requireRecordTable || Boolean(output.referencedTable)) && outputSelectable(output, expectedType, referencedTable, allowedTypes, tableParents);
                const expandable = outputExpandable(output);
                // Incompatible leaves are filtered out; a reference that cannot be picked itself
                // stays visible only as a way to navigate to a compatible field.
                if (!selectable && !expandable) return null;
                const open = Boolean(expanded[output.path]);
                return (
                    <Box key={output.path}>
                        <ListItemButton
                            dense
                            role="option"
                            aria-label={label}
                            aria-disabled={!selectable || undefined}
                            selected={selectedPath === output.path}
                            onClick={() => {
                                if (selectable) onPick(output);
                                else setExpanded((prev) => ({ ...prev, [output.path]: !prev[output.path] }));
                            }}
                            sx={{ pl: 2 + output.depth * 1.5, opacity: selectable ? 1 : 0.7 }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap>{label}</Typography>
                                <Typography variant="caption" color="text.secondary">{pickerTypeLabel(t, output.type)}</Typography>
                            </Box>
                            {expandable && (
                                <IconButton
                                    size="small"
                                    aria-label={String(t("fluss.data.picker.expand", { defaultValue: "Expandir {{label}}", label }))}
                                    aria-expanded={open}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setExpanded((prev) => ({ ...prev, [output.path]: !prev[output.path] }));
                                    }}
                                >
                                    {open ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                                </IconButton>
                            )}
                        </ListItemButton>
                        {open && expandable && output.depth < MAX_PATH_DEPTH && (
                            <ChildRows
                                output={output}
                                expectedType={expectedType}
                                allowedTypes={allowedTypes}
                                requireRecordTable={requireRecordTable}
                                referencedTable={referencedTable}
                                tableParents={tableParents}
                                selectedPath={selectedPath}
                                onPick={onPick}
                                onRowsChanged={onRowsChanged}
                            />
                        )}
                    </Box>
                );
            })}
        </>
    );
};

export const DataPicker = ({
    value,
    onChange,
    expectedType,
    allowedTypes,
    requireRecordTable,
    referencedTable,
    destinationField,
    allowFixed = true,
    literalTemplate = false,
    includeRecord = true,
    recordOnly = false,
    recordTable,
    recordSourceLabel,
    extraGroups,
    catalog,
    error = false,
    helperText,
    ariaLabel,
    disabled = false,
    fieldPath,
    onResolved,
}: DataPickerProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const contextCatalog = useDataCatalogContext();
    const activeCatalog = catalog || contextCatalog;
    const destinationType = expectedType || destinationField?.type;
    const currentDate = parseCurrentDateToken(value);
    const showCurrentDate = !requireRecordTable && ["DATE", "DATETIME", "TEXT"].includes(destinationType || "");
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [search, setSearch] = useState("");
    const searchRef = useRef<HTMLInputElement | null>(null);
    const focusSearch = useCallback((input: HTMLInputElement | null) => {
        searchRef.current = input;
        input?.focus();
    }, []);
    const popoverAction = useRef<{ updatePosition: () => void } | null>(null);
    const onRowsChanged = useCallback(() => popoverAction.current?.updatePosition(), []);
    const [searchResults, setSearchResults] = useState<{ query: string; groups: PickerGroup[] } | null>(null);
    const [literalMode, setLiteralMode] = useState(false);
    const [focusTemplateEnd, setFocusTemplateEnd] = useState(false);
    const isReference = isDataReference(value);
    const isLiteral = !isReference && !currentDate && (value !== undefined || literalMode);

    // `record.` can belong to a table other than the process anchor (a node-local work record).
    const overrideColumns = useTableColumns(recordTable && recordTable !== activeCatalog.record?.table ? recordTable : undefined);
    const tableParents = useTableParents(Boolean(referencedTable));
    const effectiveRecordTable = recordTable || activeCatalog.record?.table;
    const [tableLabel, setTableLabel] = useState<{ table: string; label: string } | null>(null);
    useEffect(() => {
        if (!anchor || !effectiveRecordTable) return;
        let active = true;
        listFlussMetadata("sys_tables").then((tables) => {
            const table = tables.find((entry) => entry.name === effectiveRecordTable);
            if (active && table) setTableLabel({ table: effectiveRecordTable, label: translateMetadataLabel(t, table) });
        }).catch(() => undefined);
        return () => { active = false; };
    }, [anchor, effectiveRecordTable, t]);
    const groups = useMemo<PickerGroup[]>(() => buildPickerGroups(activeCatalog, t, {
        includeRecord,
        recordOnly,
        extraGroups,
        recordTableLabel: tableLabel && tableLabel.table === effectiveRecordTable ? tableLabel.label : undefined,
        recordSourceLabel,
        ...(recordTable && recordTable !== activeCatalog.record?.table
            ? { recordTable, recordOutputs: overrideColumns.columns.map(scopeOptionToOutput) }
            : {}),
    }), [activeCatalog, t, includeRecord, recordOnly, extraGroups, recordTable, recordSourceLabel, overrideColumns.columns, tableLabel, effectiveRecordTable]);
    const groupsSignature = JSON.stringify(groups);
    useEffect(() => {
        const query = normalizeSearch(search.trim());
        if (!anchor || !query) return;
        let active = true;
        const matchingOutputs = async (outputs: PickerOutput[], prefix = ""): Promise<PickerOutput[]> => {
            const matches = await Promise.all(outputs.map(async (output) => {
                const label = pickerOutputLabel(t, output);
                const fullLabel = prefix ? `${prefix} › ${label}` : label;
                const ownMatch = normalizeSearch(`${label} ${output.key}`).includes(query);
                const found: PickerOutput[] = ownMatch ? [{ ...output, label: fullLabel, labelKey: undefined }] : [];
                if (outputExpandable(output) && output.referencedTable) {
                    const columns = await loadTableColumns(output.referencedTable);
                    const children = columns.map((column): PickerOutput => ({
                        ...scopeOptionToOutput(column), path: `${output.path}.${column.name}`, depth: output.depth + 1,
                    }));
                    found.push(...await matchingOutputs(children, fullLabel));
                }
                return found;
            }));
            return matches.flat();
        };
        void Promise.all(groups.map(async (group) => ({ ...group, outputs: await matchingOutputs(group.outputs) })))
            .then((results) => { if (active) setSearchResults({ query, groups: results }); });
        return () => { active = false; };
        // The catalog's content, rather than the translator function's identity, drives a new search.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchor, groupsSignature, search]);
    const info = useDataPathInfo(value, groups, t);

    const resolvedKey = info ? `${info.path}|${info.type}|${info.referencedTable}|${info.columnId}|${info.known}|${JSON.stringify(info.options)}` : "";
    const onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;
    useEffect(() => {
        if (info && info.known) onResolvedRef.current?.({
            path: info.path, label: info.label, type: info.type,
            referencedTable: info.referencedTable, options: info.options, columnId: info.columnId,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedKey]);

    // Chip details for a fixed value: CHOICE/REFERENCE values are internal ids, not readable text.
    const destinationColumn = destinationField?.column;
    const choiceColumn = destinationColumn?.column_type === "CHOICE" ? destinationColumn : undefined;
    const { data: choiceOptions = [] } = useChoiceOptions(choiceColumn);
    const literalReferenceTable = destinationColumn?.column_type === "REFERENCE"
        ? destinationColumn.referenced_table_name : undefined;
    const { displayColumnName } = useReferenceDisplayColumn(literalReferenceTable);
    const { data: literalReferenceOptions = [] } = useReferenceOptions({
        tableName: literalReferenceTable,
        selectedId: isLiteral && typeof value === "string" ? value : null,
        displayColumnName,
        loadOptions: false,
    });
    const literalDetail = () => {
        if (choiceColumn) {
            const match = choiceOptions.find((option) => option.value === value);
            if (match) return match.label;
        }
        if (literalReferenceTable) {
            const match = literalReferenceOptions.find((option: any) => option.id === value);
            if (match) return getReferenceOptionLabel(t, match, displayColumnName);
        }
        return String(value ?? t("fluss.builder.scope.empty_value", { defaultValue: "Empty" }));
    };

    const close = () => { setAnchor(null); setSearch(""); };
    const pick = (output: PickerOutput) => {
        setLiteralMode(false);
        onChange(output.path, {
            path: output.path, label: pickerOutputLabel(t, output), type: output.type,
            referencedTable: output.referencedTable, options: output.options, columnId: output.columnId,
        });
        close();
    };
    const clear = () => { setLiteralMode(false); onChange(undefined); close(); };
    const chooseFixed = () => {
        setLiteralMode(true);
        setFocusTemplateEnd(false);
        onChange(defaultLiteral(destinationType));
        close();
    };
    const setCurrentDate = (base: "now" | "today", sign: "+" | "-", amount: number, unit: CurrentDateUnit) => {
        onChange(amount > 0 ? `@${base}${sign}${amount}${unit}` : `@${base}`);
    };
    const unitNames: Record<CurrentDateUnit, [string, string, string]> = {
        minute: ["minute", "minuto", "minutos"], hour: ["hour", "hora", "horas"], day: ["day", "dia", "dias"],
        week: ["week", "semana", "semanas"], month: ["month", "mês", "meses"], year: ["year", "ano", "anos"],
    };
    const currentDateLabel = (base: "now" | "today") => String(t(`fluss.data.picker.current.${base}`, {
        defaultValue: base === "now" ? "Agora" : "Hoje",
    }));
    const currentDateChip = currentDate && (currentDate.amount > 0
        ? `${currentDateLabel(currentDate.base)} ${currentDate.sign === "-" ? "−" : "+"} ${currentDate.amount} ${String(t(`fluss.data.picker.current.unit.${currentDate.unit}.${currentDate.amount === 1 ? "one" : "many"}`, {
            defaultValue: unitNames[currentDate.unit][currentDate.amount === 1 ? 1 : 2],
        }))}` : currentDateLabel(currentDate.base));

    const fixedLabel = String(literalTemplate
        ? t("fluss.builder.scope.template_text", { defaultValue: "Texto com dados" })
        : t("fluss.builder.scope.literal", { defaultValue: "Fixed value" }));
    const renderChip = () => {
        if (isReference) {
            return <Chip
                size="small"
                data-testid="data-picker-chip"
                label={info?.parts.join(" › ") || String(value)}
                sx={{
                    maxWidth: "100%", color: "primary.main", fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                }}
            />;
        }
        if (currentDate) return <Chip size="small" data-testid="data-picker-chip" label={currentDateChip} />;
        if (isLiteral) {
            return <Chip
                size="small"
                data-testid="data-picker-chip"
                label={`${fixedLabel} · ${literalDetail()}`}
                sx={{ maxWidth: "100%", color: "text.secondary", fontWeight: 600, bgcolor: alpha(theme.palette.text.secondary, 0.08) }}
            />;
        }
        return <Box component="span" sx={{ color: "text.secondary" }}>
            {t("fluss.builder.scope.placeholder", { defaultValue: "Select a source" })}
        </Box>;
    };

    const literalControl = () => {
        if (literalTemplate || destinationType === "TEMPLATE_TEXT") {
            return <TemplateTextField
                label={fixedLabel}
                value={typeof value === "string" ? value : String(value ?? "")}
                onChange={(next) => onChange(next)}
                disabled={disabled}
                fieldPath={fieldPath}
                focusAtEnd={focusTemplateEnd}
                multiline={["TEXT", "RICH_HTML"].includes(destinationField?.column?.column_type || "")}
            />;
        }
        if (destinationColumn && VALUE_FIELD_TYPES.includes(destinationColumn.column_type)) {
            return (
                <ValueField
                    column={asValueFieldColumn(destinationColumn)}
                    value={value ?? null}
                    onChange={(next) => onChange(normalizeLiteralValue(next))}
                    disabled={disabled}
                />
            );
        }
        if (destinationType === "BOOLEAN") {
            return (
                <Select
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={String(Boolean(value))}
                    inputProps={{ "aria-label": fixedLabel }}
                    onChange={(event) => onChange(event.target.value === "true")}
                >
                    <MenuItem value="true">{t("fluss.common.yes", { defaultValue: "Yes" })}</MenuItem>
                    <MenuItem value="false">{t("fluss.common.no", { defaultValue: "No" })}</MenuItem>
                </Select>
            );
        }
        const isNumber = ["INTEGER", "DECIMAL", "FLOAT", "CURRENCY"].includes(destinationType || "");
        return (
            <TextField
                size="small"
                fullWidth
                disabled={disabled}
                type={isNumber ? "number" : destinationType === "DATE" ? "date" : destinationType === "DATETIME" ? "datetime-local" : "text"}
                value={value ?? ""}
                inputProps={{ "aria-label": fixedLabel }}
                onChange={(event) => onChange(isNumber ? Number(event.target.value) : event.target.value)}
            />
        );
    };

    const selectedPath = isReference ? String(value) : "";
    const needle = normalizeSearch(search.trim());
    const visibleGroups = needle ? (searchResults?.query === needle ? searchResults.groups : []) : groups;
    return (
        <Box sx={{ width: "100%", minWidth: 0 }} data-testid="data-picker">
            <FormControl size="small" fullWidth error={error}>
                <Stack direction="row" spacing={0.5} alignItems="center"><ButtonBase
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={Boolean(anchor)}
                    aria-label={ariaLabel || String(t("fluss.builder.scope.aria_label", { defaultValue: "Value source" }))}
                    onClick={(event) => setAnchor(event.currentTarget)}
                    sx={{
                        justifyContent: "space-between", width: "100%", minHeight: 40, px: 1.25, py: 0.75,
                        border: "1px solid", borderColor: error ? "error.main" : "divider", borderRadius: 1,
                        textAlign: "left", "&:hover": { borderColor: "text.primary" },
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>{renderChip()}</Box>
                    <ExpandMore fontSize="small" />
                </ButtonBase>
                {literalTemplate && isReference && !disabled && <IconButton size="small"
                    aria-label={String(t("fluss.builder.scope.append_text", { defaultValue: "Acrescentar texto" }))}
                    onClick={() => { setFocusTemplateEnd(true); setLiteralMode(true); onChange(`{{${value}}}`); }}>
                    <Add fontSize="small" />
                </IconButton>}</Stack>
                {helperText && <FormHelperText sx={{ fontSize: 12 }}>{helperText}</FormHelperText>}
            </FormControl>
            <Popover
                open={Boolean(anchor)}
                transitionDuration={0}
                action={popoverAction}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{ sx: { width: 380, maxWidth: "90vw", maxHeight: "min(460px, calc(100dvh - 32px))", overflowY: "auto" } }}
            >
                <Stack spacing={0.5} sx={{ p: 1 }}>
                    <TextField
                        size="small"
                        autoFocus
                        inputRef={focusSearch}
                        value={search}
                        onKeyDown={(event) => event.stopPropagation()}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder={String(t("fluss.data.picker.search", { defaultValue: "Buscar dado" }))}
                        inputProps={{ "aria-label": String(t("fluss.data.picker.search", { defaultValue: "Buscar dado" })) }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                    />
                    <List dense disablePadding role="listbox" aria-label={String(t("fluss.data.picker.options", { defaultValue: "Dados disponíveis" }))}>
                        {(isReference || isLiteral || currentDate) && (
                            <ListItemButton dense role="option" data-testid="data-picker-clear" onClick={clear}>
                                <em>{t("fluss.builder.scope.clear", { defaultValue: "— None —" })}</em>
                            </ListItemButton>
                        )}
                        {allowFixed && literalTemplate && (
                            <Box data-testid="data-picker-group-fixed">
                                <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>{fixedLabel}</ListSubheader>
                                <ListItemButton dense role="option" onClick={chooseFixed}>
                                    {t("fluss.builder.scope.enter_template_text", { defaultValue: "Escrever texto e inserir dados" })}
                                </ListItemButton>
                            </Box>
                        )}
                        {visibleGroups.map((group) => {
                            const visible = group.outputs.some((output) => {
                                const label = pickerOutputLabel(t, output);
                                return ((!requireRecordTable || Boolean(output.referencedTable)) && outputSelectable(output, expectedType, referencedTable, allowedTypes, tableParents)) || outputExpandable(output);
                            });
                            if (!visible) return null;
                            return (
                                <Box key={group.key} data-testid={`data-picker-group-${group.key}`}>
                                    <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>{group.label}</ListSubheader>
                                    <OutputRows
                                        outputs={group.outputs}
                                        expectedType={expectedType}
                                        allowedTypes={allowedTypes}
                                        requireRecordTable={requireRecordTable}
                                        referencedTable={referencedTable}
                                        tableParents={tableParents}
                                        selectedPath={selectedPath}
                                        search=""
                                        onPick={pick}
                                        onRowsChanged={onRowsChanged}
                                    />
                                </Box>
                            );
                        })}
                        {showCurrentDate && (!needle || ["agora", "hoje", "data atual"].some((label) => label.includes(needle))) && (
                            <Box data-testid="data-picker-group-current-date">
                                <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>{t("fluss.data.picker.current.group", { defaultValue: "Data atual" })}</ListSubheader>
                                {(["now", "today"] as const).map((base) => (
                                    <ListItemButton key={base} dense role="option" aria-label={currentDateLabel(base)}
                                        onClick={() => { setLiteralMode(false); setCurrentDate(base, "+", 0, "day"); close(); }}>
                                        {currentDateLabel(base)}
                                    </ListItemButton>
                                ))}
                            </Box>
                        )}
                        {allowFixed && !literalTemplate && (
                            <Box data-testid="data-picker-group-fixed">
                                <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>{fixedLabel}</ListSubheader>
                                <ListItemButton dense role="option" onClick={chooseFixed}>
                                    {t("fluss.builder.scope.enter_fixed", { defaultValue: "Enter a fixed value" })}
                                </ListItemButton>
                            </Box>
                        )}
                    </List>
                </Stack>
            </Popover>
            {allowFixed && isLiteral && <Box sx={{ mt: 1 }}>{literalControl()}</Box>}
            {currentDate && <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField select size="small" value={currentDate.sign}
                    inputProps={{ "aria-label": String(t("fluss.data.picker.current.sign", { defaultValue: "Sinal" })) }}
                    onChange={(event) => setCurrentDate(currentDate.base, event.target.value as "+" | "-", currentDate.amount || 1, currentDate.unit)}>
                    <MenuItem value="+">+</MenuItem><MenuItem value="-">−</MenuItem>
                </TextField>
                <TextField size="small" type="number" value={currentDate.amount} inputProps={{ min: 0, step: 1,
                    "aria-label": String(t("fluss.data.picker.current.amount", { defaultValue: "Deslocamento" })) }}
                    onChange={(event) => setCurrentDate(currentDate.base, currentDate.sign,
                        Math.max(0, Math.trunc(Number(event.target.value) || 0)), currentDate.unit)} sx={{ width: 110 }} />
                <TextField select size="small" value={currentDate.unit} sx={{ flex: 1 }}
                    inputProps={{ "aria-label": String(t("fluss.data.picker.current.unit", { defaultValue: "Unidade" })) }}
                    onChange={(event) => setCurrentDate(currentDate.base, currentDate.sign, currentDate.amount || 1, event.target.value as CurrentDateUnit)}>
                    {CURRENT_DATE_UNITS.filter((unit) => destinationType !== "DATE" || !["minute", "hour"].includes(unit)).map((unit) => (
                        <MenuItem key={unit} value={unit}>{t(`fluss.data.picker.current.units.${unit}`, { defaultValue: unitNames[unit][1] })}</MenuItem>
                    ))}
                </TextField>
            </Stack>}
        </Box>
    );
};

export default DataPicker;
